import { _ } from '@ngx-translate/core';
import * as z from 'zod';
import { Column, FormField } from './column.model';

interface UserLogin {
  id: string;
  name: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  permissions: string[];
  roles: string[];
}

const bitToBoolean = z.codec(
  z.number(), // input schema
  z.boolean(), // output schema
  {
    decode: (nVal) => !!nVal,
    encode: (bVal) => +bVal,
  },
);

const UserSchema = z.object({
  id: z.string(),
  email: z.email(),
  salutation: z.string().nullable(),
  active: bitToBoolean,
  name: z.string(),
  title: z.string().nullable(),
  createdAt: z.string().nullable().optional(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  companyName: z.string().nullable(),
  companyId: z.number().nullable(),
  roles: z.string().array(),
  hasPassword: z.boolean().optional().default(false),
});

type User = z.infer<typeof UserSchema>;

const columns: Column<keyof User>[] = [
  {
    header: 'column.firstName',
    field: 'firstName',
    filterField: 'firstName',
    sortField: 'firstName',
    type: 'text',
  },
  {
    header: 'column.lastName',
    field: 'lastName',
    filterField: 'lastName',
    sortField: 'lastName',
    type: 'text',
  },
  {
    header: 'column.email',
    field: 'email',
    filterField: 'email',
    sortField: 'email',
    type: 'text',
  },
  {
    header: 'column.roles',
    field: 'roles',
    filterField: 'roles',
    type: 'text',
    displayType: 'tags',
    placeholder: 'placeholder.selectRole',
  },
  {
    header: 'column.active',
    field: 'active',
    filterField: 'active',
    sortField: 'active',
    type: 'boolean',
  },
  {
    header: 'column.company',
    field: 'companyName',
    filterField: 'company',
    type: 'text',
  },
  {
    header: 'column.salutation',
    field: 'salutation',
    filterField: 'salutation',
    sortField: 'salutation',
    type: 'text',
  },
  {
    header: 'column.title',
    field: 'title',
    filterField: 'title',
    sortField: 'title',
    type: 'text',
  },
  {
    header: 'column.createdAt',
    field: 'createdAt',
    filterField: 'created_at',
    sortField: 'created_at',
    type: 'date',
    displayType: 'date',
  },
];

const formFields: FormField<keyof User>[] = [
  { field: 'salutation', header: 'column.salutation', type: 'text' },
  { field: 'title', header: 'column.title', type: 'text' },
  { field: 'firstName', header: 'column.firstName', type: 'text' },
  { field: 'lastName', header: 'column.lastName', type: 'text' },
  { field: 'email', header: 'column.email', type: 'text' },
  {
    field: 'companyId',
    header: 'column.company',
    type: 'select',
    optionsEndpoint: 'manage/companies',
    filter: true,
  },
  { field: 'active', header: 'column.active', type: 'boolean' },
  {
    field: 'roles',
    header: 'column.roles',
    type: 'tags',
    optionsEndpoint: 'roles',
  },
];

const globalFilterFields = [
  'companyName',
  'email',
  'firstName',
  'lastName',
  'name',
  'salutation',
  'title',
];

_([
  'column.email',
  'column.salutation',
  'column.title',
  'column.firstName',
  'column.lastName',
  'column.company',
  'column.active',
  'column.roles',
  'column.name',
  'column.createdAt',
  'placeholder.selectRole',
]);

const User = UserSchema.meta({
  columns,
  formFields,
  globalFilterFields,
  emptyRecord: (): User => ({
    id: '',
    name: '',
    email: '',
    firstName: '',
    lastName: '',
    salutation: '',
    title: '',
    companyId: null,
    companyName: '',
    active: true,
    roles: [],
    hasPassword: false,
  }),
});

export { bitToBoolean, User, UserLogin };
